
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import DepartmentRepositoryInterface from '@/services/Departments/Interfaces';
import { FilterRequestData } from '@/services/QC-V2/chat/interfaces';

interface FilterItemInterface {
  text: string,
  value: string,
}

enum FilterType {
  MENU,
  AUTOCOMPLETE,
  INPUT,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  type: FilterType,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'userRepository',
    'departmentRepository',
  ],
})
export default class EscalationsFilters extends Vue {
  private userRepository!: UserRepoInterface;
  private departmentRepository!: DepartmentRepositoryInterface;

  private selectedFilters: FilterInterface[] = [];

  private filters: FilterInterface[] = [];

  private customDates: string[] = [];

  private keepCustomPeriodActive(filter: FilterInterface, index: number): void {
    setTimeout(() => {
      if (filter.value === 'escalation.timestamp' && filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
        this.selectedFilters[index] = filter;
      }
    }, 10);
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.addChatIdFilter();
      this.addTicketNumberFilter();
      await this.getSupportAgents();
      await this.getQAStaff();
      this.addDatesFilter();
      this.addPriorityFilter();
      this.addStatusFilter();
      this.addRatesFilter();
      this.selectedFilters = this.filters;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getSupportAgents(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:manager')) {
      return;
    }

    const departments = await this.departmentRepository.getAllDepartments();
    const supportDepartment = departments.find((department) => department.name.toLowerCase() === 'support');
    if (!supportDepartment) {
      this.$errorDialog.show('\'Support\' Department not found...');
      return;
    }

    const supportAgents = await this.userRepository.getUsersByDepartment(supportDepartment._id);
    this.filters.push(
      {
        text: 'Agent',
        value: 'agent.email',
        icon: 'mdi-account',
        type: FilterType.AUTOCOMPLETE,
        items: supportAgents.map((agent) => ({ text: agent.email, value: agent.email })),
        selected: { text: '', value: '' },
      },
    );
  }

  private async getQAStaff(): Promise<void> {
    const [qaManagers, shiftManagers] = await Promise.all([
      this.userRepository.getQaManagers(),
      this.userRepository.getShiftManagers(),
    ]);

    const staff = [...qaManagers, ...shiftManagers];

    this.filters.push(
      {
        text: 'Escalated From',
        value: 'escalation.escalatedFrom',
        icon: 'mdi-account-tie',
        type: FilterType.AUTOCOMPLETE,
        items: staff.map((agent) => ({ text: agent.email, value: agent.id })),
        selected: { text: '', value: '' },
      },
    );

    this.filters.push(
      {
        text: 'Escalated To',
        value: 'escalation.to',
        icon: 'mdi-account-arrow-up-outline',
        type: FilterType.AUTOCOMPLETE,
        items: staff.map((agent) => ({ text: agent.email, value: agent.id })),
        selected: { text: '', value: '' },
      },
    );
  }

  private addChatIdFilter(): void {
    const filter: FilterInterface = {
      text: 'Chat ID',
      value: 'chat.id',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'chat.id', value: '' },
    };

    this.filters.push(filter);
  }

  private addTicketNumberFilter(): void {
    const filter: FilterInterface = {
      text: 'Ticket Number',
      value: 'ticket.ticketNumber',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'ticket.ticketNumber', value: '' },
    };

    this.filters.push(filter);
  }

  private addStatusFilter(): void {
    this.filters.push(
      {
        text: 'Status',
        value: 'escalation.status',
        icon: 'mdi-state-machine',
        type: FilterType.MENU,
        items: [
          { text: 'OPEN', value: 'open' },
          { text: 'IN PROGRESS', value: 'in-progress' },
          { text: 'REVIEWED', value: 'reviewed' },
        ],
        selected: { text: '', value: '' },
      },
    );
  }

  private addPriorityFilter(): void {
    this.filters.push(
      {
        text: 'Priority',
        value: 'escalation.priority',
        icon: 'mdi-priority-high',
        type: FilterType.MENU,
        items: [
          { text: 'LOW', value: 'low' },
          { text: 'NORMAL', value: 'normal' },
          { text: 'MEDIUM', value: 'medium' },
          { text: 'HIGH', value: 'high' },
          { text: 'CRITICAL', value: 'critical' },
        ],
        selected: { text: '', value: '' },
      },
    );
  }

  private addDatesFilter(): void {
    const dateFilter: FilterInterface = {
      text: 'Date',
      value: 'escalation.timestamp',
      icon: 'mdi-calendar',
      type: FilterType.MENU,
      items: [
        { text: 'TODAY', value: 'today' },
        { text: 'YESTERDAY', value: 'yesterday' },
        { text: 'LAST 7 DAYS', value: '7 days' },
        { text: 'LAST 30 DAYS', value: '30 days' },
        { text: 'LAST MONTH', value: 'last month' },
        { text: 'CURRENT MONTH', value: 'current month' },
        { text: 'CUSTOM PERIOD', value: 'custom period' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(dateFilter);
  }

  private addRatesFilter(): void {
    const rateFilter: FilterInterface = {
      text: 'Rate',
      value: 'chat.rate',
      icon: 'mdi-thumbs-up-down-outline',
      type: FilterType.MENU,
      items: [
        { text: 'NOT RATED', value: '0' },
        { text: 'RATED BAD', value: '1' },
        { text: 'RATED GOOD', value: '2' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(rateFilter);
  }

  private async applyFilter(): Promise<void> {
    const filterData: FilterRequestData = {};
    this.filters.forEach((filter) => {
      if (!filter.selected || filter.selected.text === '') {
        return;
      }

      if (filter.value === 'escalation.timestamp') {
        filterData[filter.value] = this.getDateFilterData();
        return;
      }

      filterData[filter.value] = filter.selected.value;
    });

    this.$emit('filter-escalations', filterData);
  }

  private getDateFilterData(): string | undefined {
    const dateFilter = this.filters.find((filter) => filter.value === 'escalation.timestamp' && filter.selected);
    if (!dateFilter || !dateFilter.selected || !dateFilter.selected.value) {
      return undefined;
    }

    const selected = dateFilter.selected.value;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;

      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    startDate.setMinutes(startDate.getTimezoneOffset());
    endDate.setMinutes(endDate.getTimezoneOffset());
    return `${this.convertDateToString(startDate)},${this.convertDateToString(endDate)}`;
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se');
  }

  private async removeFilter(filter: FilterInterface, filterIndex: number): Promise<void> {
    this.selectedFilters[filterIndex].selected = { text: '', value: '' };
    this.selectedFilters = this.selectedFilters.filter((f) => f.text !== filter.text);
    await this.applyFilter();
  }
}
